/* eslint-disable react-hooks/exhaustive-deps */
import { KTSVG } from '../../_metronic/helpers'
import React, { useEffect, useState } from 'react'
import { Button, Table } from 'react-bootstrap-v5'
import Swal from 'sweetalert2'
import { getRequestOptions, postRequestOptions } from '../helpers/Fetchwrapper'
import validationErrorMsg from '../helpers/ValidationErrorMsg'
import Select from 'react-select'
import { useHistory, useParams } from 'react-router-dom'
import { formatDecimalQuantity } from '../helpers/GlobalHelper'

interface RouteParams {
	id: string;
}

interface SelectOption {
	label: string;
	value: number | string;
}

interface ProductOption extends SelectOption {
	itemCode: string;
	unitName: string;
	boms: { idItemBoms: number; bomName: string }[];
}

interface MaterialRow {
	idItems: number;
	itemName: string;
	categoryName: string;
	percentage: number;
	quantity: number;
	unitName: string;
	idWarehouses: number | '';
	availableQuantity: number;
}

const calcMaterialQty = (batchWeightKg: number, percentage: number) => {
	return parseFloat(((batchWeightKg * percentage) / 100).toFixed(5))
}

const calcWeightKg = (productionQty: number, quantityLtr: number, weightKg: number) => {
	if (quantityLtr > 0) {
		return parseFloat(((weightKg / quantityLtr) * productionQty).toFixed(5))
	}
	return weightKg || 0
}

const BLENDING_PROJECT_ID = 1
const BASE_OIL_CATEGORY = 'Base Oil'
const ADDITIVE_CATEGORY = 'Additive'

const resolveRowWarehouse = (
	categoryName: string,
	defaultWarehouse: SelectOption | undefined,
	baseOil: SelectOption | null,
	additive: SelectOption | null,
): number | '' => {
	if (categoryName === BASE_OIL_CATEGORY && baseOil) {
		return Number(baseOil.value)
	}
	if (categoryName === ADDITIVE_CATEGORY && additive) {
		return Number(additive.value)
	}
	return defaultWarehouse ? Number(defaultWarehouse.value) : ''
}

// The last Base Oil / Additive warehouse picked is remembered per project and preselected next time
const groupWarehouseKey = (idProjects: number, categoryName: string) => `blendingOrder.warehouse.${idProjects}.${categoryName}`

const readSavedWarehouse = (idProjects: number, categoryName: string, options: SelectOption[]): SelectOption | null => {
	try {
		const saved = localStorage.getItem(groupWarehouseKey(idProjects, categoryName))
		return options.find((option) => String(option.value) === saved) || null
	} catch (error) {
		return null
	}
}

const storeWarehouse = (idProjects: number, categoryName: string, option: SelectOption | null) => {
	try {
		if (option) {
			localStorage.setItem(groupWarehouseKey(idProjects, categoryName), String(option.value))
		} else {
			localStorage.removeItem(groupWarehouseKey(idProjects, categoryName))
		}
	} catch (error) {
		console.log(error, 'catch the hoop')
	}
}

function BlendingOrderCreate() {
	const history = useHistory()
	const { id }: RouteParams = useParams()
	const isEdit = Boolean(id)

	const [projects, setProjects] = useState<SelectOption[]>([])
	const [project, setProject] = useState<SelectOption | null>(null)
	const [products, setProducts] = useState<ProductOption[]>([])
	const [product, setProduct] = useState<ProductOption | null>(null)
	const [bomOptions, setBomOptions] = useState<SelectOption[]>([])
	const [selectedBom, setSelectedBom] = useState<SelectOption | null>(null)
	const [warehouses, setWarehouses] = useState<SelectOption[]>([])
	const [baseOilWarehouse, setBaseOilWarehouse] = useState<SelectOption | null>(null)
	const [additiveWarehouse, setAdditiveWarehouse] = useState<SelectOption | null>(null)
	const [productionDate, setProductionDate] = useState('')
	const [productionQuantity, setProductionQuantity] = useState<number | ''>('')
	const [weightInKg, setWeightInKg] = useState<number | ''>('')
	const [batchNo, setBatchNo] = useState('')
	const [remarks, setRemarks] = useState('')
	const [productCode, setProductCode] = useState('')
	const [quantityLtr, setQuantityLtr] = useState(0)
	const [baseWeightKg, setBaseWeightKg] = useState(0)
	const [materials, setMaterials] = useState<MaterialRow[]>([])
	const [saving, setSaving] = useState(false)

	useEffect(() => {
		getProjects()
		getProducts()
	}, [])

	useEffect(() => {
		if (isEdit && products.length > 0) {
			loadOrder(id)
		}
	}, [isEdit, id, products.length])

	const getProjects = () => {
		fetch(process.env.REACT_APP_API_URL + 'lead/projects', getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => {
				const options: SelectOption[] = (resp.data || [])
					.filter((item: any) => Number(item.id) === BLENDING_PROJECT_ID)
					.map((item: any) => ({
						label: item.name,
						value: item.id,
					}))
				setProjects(options)
				if (!isEdit && options.length > 0) {
					const selected = options[0]
					setProject(selected)
					getWarehouses(Number(selected.value), (whOptions) => applySavedGroupWarehouses(Number(selected.value), whOptions))
				}
			})
			.catch((error) => console.log(error, 'catch the hoop'))
	}

	const getProducts = () => {
		fetch(process.env.REACT_APP_API_URL + 'blending-orders/products', getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => {
				const options: ProductOption[] = (resp.data || []).map((item: any) => ({
					label: item.itemName,
					value: item.idItems,
					itemCode: item.itemCode,
					unitName: item.unitName,
					boms: item.boms || [],
				}))
				setProducts(options)
			})
			.catch((error) => console.log(error, 'catch the hoop'))
	}

	const getWarehouses = (idProjects: number, onLoaded?: (options: SelectOption[]) => void) => {
		if (!idProjects) {
			setWarehouses([])
			onLoaded && onLoaded([])
			return
		}
		fetch(process.env.REACT_APP_API_URL + 'warehouse/' + idProjects + '/list', getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => {
				const options: SelectOption[] = (resp.success && Array.isArray(resp.data))
					? resp.data.map((item: any) => ({
						label: item.name,
						value: item.id_warehouses,
					}))
					: []
				setWarehouses(options)
				onLoaded && onLoaded(options)
			})
			.catch(() => {
				setWarehouses([])
				onLoaded && onLoaded([])
			})
	}

	const applySavedGroupWarehouses = (idProjects: number, whOptions: SelectOption[]) => {
		const baseOil = readSavedWarehouse(idProjects, BASE_OIL_CATEGORY, whOptions)
		const additive = readSavedWarehouse(idProjects, ADDITIVE_CATEGORY, whOptions)
		setBaseOilWarehouse(baseOil)
		setAdditiveWarehouse(additive)
		return { baseOil, additive }
	}

	const fetchItemStock = async (idProjects: number, idWarehouses: number, idItems: number, excludeId?: string) => {
		const params = new URLSearchParams({
			id_projects: String(idProjects),
			id_warehouses: String(idWarehouses),
			id_items: String(idItems),
		})
		if (excludeId) {
			params.append('exclude_blending_order_id', excludeId)
		}
		const response = await fetch(
			process.env.REACT_APP_API_URL + 'blending-orders/item-stock?' + params.toString(),
			getRequestOptions()
		)
		const resp = await response.json()
		const qty = parseFloat(resp.data?.availableQuantity) || 0
		return parseFloat(qty.toFixed(2))
	}

	const applyStockToMaterials = async (rows: MaterialRow[], idProjects: number, excludeId?: string) => {
		const updated = await Promise.all(rows.map(async (row) => {
			if (!row.idWarehouses) {
				return { ...row, availableQuantity: 0 }
			}
			const availableQuantity = await fetchItemStock(idProjects, Number(row.idWarehouses), row.idItems, excludeId)
			return { ...row, availableQuantity }
		}))
		setMaterials(updated)
	}

	const loadBomData = async (
		idItems: number,
		idItemBoms: number,
		productionQty: number,
		warehouseOptions: SelectOption[],
		idProjects?: number,
		groupWarehouses?: { baseOil: SelectOption | null; additive: SelectOption | null },
	) => {
		const response = await fetch(
			process.env.REACT_APP_API_URL + `blending-orders/bom-data/${idItems}?id_item_bom=${idItemBoms}`,
			getRequestOptions()
		)
		const resp = await response.json()
		const qtyLtr = parseFloat(resp.data?.quantityLtr) || 0
		const wKg = parseFloat(resp.data?.weightKg) || 0
		setQuantityLtr(qtyLtr)
		setBaseWeightKg(wKg)
		const batchWeightKg = productionQty ? calcWeightKg(productionQty, qtyLtr, wKg) : 0
		setWeightInKg(productionQty ? batchWeightKg : (wKg || ''))

		const defaultWarehouse = warehouseOptions[0]
		const baseOil = groupWarehouses?.baseOil ?? null
		const additive = groupWarehouses?.additive ?? null
		const rows: MaterialRow[] = (resp.items || []).map((row: any) => ({
			idItems: row.idItems,
			itemName: row.itemName,
			categoryName: row.categoryName,
			percentage: parseFloat(row.percentage) || 0,
			quantity: productionQty ? calcMaterialQty(batchWeightKg, parseFloat(row.percentage) || 0) : 0,
			unitName: row.unitName,
			idWarehouses: resolveRowWarehouse(row.categoryName, defaultWarehouse, baseOil, additive),
			availableQuantity: 0,
		}))
		setMaterials(rows)
		if (idProjects) {
			await applyStockToMaterials(rows, idProjects, isEdit ? id : undefined)
		}
	}

	const loadOrder = (orderId: string) => {
		fetch(process.env.REACT_APP_API_URL + 'blending-orders/' + orderId, getRequestOptions())
			.then((resp) => resp.json())
			.then(async (resp) => {
				const data = resp.data
				if (data.status && data.status !== 'Draft') {
					Swal.fire({
						icon: 'warning',
						title: 'Not editable',
						text: 'Only draft blending orders can be edited.',
					}).then(() => history.push('/blending-orders'))
					return
				}
				setProject({ label: data.projectName, value: data.idProjects })
				setProductionDate(data.productionDate)
				setProductionQuantity(parseFloat(data.productionQuantity) || '')
				setWeightInKg(data.weightInKg === null || data.weightInKg === '' ? '' : parseFloat(data.weightInKg))
				setBatchNo(data.batchNo || '')
				setRemarks(data.remarks || '')
				setProductCode(data.itemCode || '')

				const selectedProduct = products.find((option) => Number(option.value) === Number(data.idItems)) || {
					label: data.itemName,
					value: data.idItems,
					itemCode: data.itemCode,
					unitName: data.unitName,
					boms: [],
				}
				setProduct(selectedProduct)
				const boms = (selectedProduct.boms || []).map((bom) => ({
					label: bom.bomName,
					value: bom.idItemBoms,
				}))
				setBomOptions(boms)
				setSelectedBom(boms.find((bom) => Number(bom.value) === Number(data.idItemBoms)) || {
					label: data.bomName,
					value: data.idItemBoms,
				})

				getWarehouses(data.idProjects, async (whOptions) => {
					const rows: MaterialRow[] = (resp.rows || []).map((row: any) => ({
						idItems: row.idItems,
						itemName: row.itemName,
						categoryName: row.categoryName,
						percentage: parseFloat(row.percentage) || 0,
						quantity: parseFloat(row.quantity) || 0,
						unitName: row.unitName,
						idWarehouses: row.idWarehouses,
						availableQuantity: 0,
					}))
					setMaterials(rows)
					await applyStockToMaterials(rows, data.idProjects, orderId)

					const bomResp = await fetch(
						process.env.REACT_APP_API_URL + `blending-orders/bom-data/${data.idItems}?id_item_bom=${data.idItemBoms}`,
						getRequestOptions()
					)
					const bomData = await bomResp.json()
					setQuantityLtr(parseFloat(bomData.data?.quantityLtr) || 0)
					setBaseWeightKg(parseFloat(bomData.data?.weightKg) || 0)
					void whOptions
				})
			})
			.catch((error) => console.log(error, 'catch the hoop'))
	}

	const handleProjectChange = (option: SelectOption | null) => {
		setProject(option)
		setWarehouses([])
		setBaseOilWarehouse(null)
		setAdditiveWarehouse(null)
		setMaterials((prev) => prev.map((row) => ({ ...row, idWarehouses: '', availableQuantity: 0 })))
		if (option?.value) {
			getWarehouses(Number(option.value), (whOptions) => {
				const defaultWarehouse = whOptions[0]
				const { baseOil, additive } = applySavedGroupWarehouses(Number(option.value), whOptions)
				setMaterials((prev) => {
					if (!defaultWarehouse) {
						return prev.map((row) => ({ ...row, idWarehouses: '', availableQuantity: 0 }))
					}
					const updated = prev.map((row) => ({
						...row,
						idWarehouses: resolveRowWarehouse(row.categoryName, defaultWarehouse, baseOil, additive),
						availableQuantity: 0,
					}))
					applyStockToMaterials(updated, Number(option.value), isEdit ? id : undefined)
					return updated
				})
			})
		}
	}

	const handleProductChange = (option: ProductOption | null) => {
		setProduct(option)
		setProductCode(option?.itemCode || '')
		const boms = (option?.boms || []).map((bom) => ({
			label: bom.bomName,
			value: bom.idItemBoms,
		}))
		setBomOptions(boms)
		const firstBom = boms[0] || null
		setSelectedBom(firstBom)
		if (option && firstBom) {
			loadBomData(Number(option.value), Number(firstBom.value), Number(productionQuantity) || 0, warehouses, project ? Number(project.value) : undefined, {
				baseOil: baseOilWarehouse,
				additive: additiveWarehouse,
			})
		} else {
			setMaterials([])
			setWeightInKg('')
		}
	}

	const handleBomChange = (option: SelectOption | null) => {
		setSelectedBom(option)
		if (product && option) {
			loadBomData(Number(product.value), Number(option.value), Number(productionQuantity) || 0, warehouses, project ? Number(project.value) : undefined, {
				baseOil: baseOilWarehouse,
				additive: additiveWarehouse,
			})
		}
	}

	const handleProductionQtyChange = (value: string) => {
		const qty = value === '' ? '' : parseFloat(value)
		setProductionQuantity(qty)
		const numericQty = typeof qty === 'number' ? qty : 0
		const batchWeightKg = calcWeightKg(numericQty, quantityLtr, baseWeightKg)
		setWeightInKg(batchWeightKg)
		setMaterials((prev) => prev.map((row) => ({
			...row,
			quantity: calcMaterialQty(batchWeightKg, row.percentage),
		})))
	}

	const handleGroupWarehouseChange = async (
		option: SelectOption | null,
		categoryName: string,
		setSelected: (value: SelectOption | null) => void,
	) => {
		setSelected(option)
		if (project?.value) {
			storeWarehouse(Number(project.value), categoryName, option)
		}
		if (!option) {
			return
		}
		const updated = materials.map((row) => {
			if (row.categoryName !== categoryName) {
				return row
			}
			return { ...row, idWarehouses: Number(option.value), availableQuantity: 0 }
		})
		if (project?.value) {
			await applyStockToMaterials(updated, Number(project.value), isEdit ? id : undefined)
		} else {
			setMaterials(updated)
		}
	}

	const handleWarehouseChange = async (option: SelectOption | null, index: number) => {
		const updated = [...materials]
		updated[index].idWarehouses = option ? option.value : ''
		updated[index].availableQuantity = 0
		if (project?.value && option?.value) {
			updated[index].availableQuantity = await fetchItemStock(
				Number(project.value),
				Number(option.value),
				updated[index].idItems,
				isEdit ? id : undefined
			)
		}
		setMaterials(updated)
	}

	const missingWarehouse = materials.some((row) => !row.idWarehouses)

	const canSave = Boolean(
		project?.value
		&& productionDate
		&& product?.value
		&& selectedBom?.value
		&& productionQuantity !== ''
		&& Number(productionQuantity) > 0
		&& batchNo.trim()
		&& materials.length > 0
		&& !missingWarehouse
		&& !saving
	)

	const handleSave = async (event: React.FormEvent) => {
		event.preventDefault()
		if (!canSave) {
			if (missingWarehouse) {
				Swal.fire({ icon: 'warning', title: 'Validation', text: 'Please select a warehouse for every material.' })
			}
			return
		}

		const confirm = await Swal.fire({
			title: 'Are you sure?',
			text: isEdit ? 'You want to update this blending order?' : 'You want to save this blending order?',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#ec008c',
			cancelButtonText: 'Cancel',
			confirmButtonText: isEdit ? 'Yes, Update it!' : 'Yes, Save it!',
		})
		if (!confirm.value) {
			return
		}

		setSaving(true)
		const payload = {
			idProjects: project?.value,
			productionDate,
			idItems: product?.value,
			idItemBoms: selectedBom?.value,
			productionQuantity,
			weightInKg: weightInKg === '' ? null : weightInKg,
			batchNo: batchNo.trim(),
			remarks: remarks.trim(),
			rows: materials,
		}

		try {
			const url = isEdit
				? `${process.env.REACT_APP_API_URL}blending-orders/update/${id}`
				: `${process.env.REACT_APP_API_URL}blending-orders`
			const response = await fetch(url, postRequestOptions(payload))
			const respData = await response.json()
			if (respData.success === true) {
				Swal.fire({
					icon: 'success',
					title: 'Success',
					text: respData.successMessage,
				}).then(() => {
					history.push('/blending-orders')
				})
			} else {
				Swal.fire({
					icon: 'error',
					title: 'Error',
					html: validationErrorMsg(respData.errors).join('<br>'),
				})
			}
		} catch (error) {
			console.log(error, 'catch the hoop')
		} finally {
			setSaving(false)
		}
	}

	return (
		<div className="card card-custom">
			<div className="card-header">
				<div className="card-title">
					<KTSVG path="/media/svg/icons/Text/Bullet-list.svg" className="svg-icon-1 svg-icon-primary" />
					<span className="text fw-bolder" style={{ marginLeft: '10px' }}>
						{isEdit ? 'Edit Blending Order' : 'Create Blending Order'}
					</span>
				</div>
			</div>
			<div className="card-body">
				<form onSubmit={handleSave}>
					<div className="row mb-3">
						<div className="col-md-4">
							<label className="form-label">Select Project</label>
							<Select
								value={project}
								onChange={handleProjectChange}
								options={projects}
								placeholder="Select Project"
								isDisabled={projects.length <= 1}
							/>
						</div>
						<div className="col-md-4">
							<label className="form-label">Production Date</label>
							<input
								type="date"
								className="form-control"
								value={productionDate}
								onChange={(e) => setProductionDate(e.target.value)}
							/>
						</div>
						<div className="col-md-4">
							<label className="form-label">Product Name</label>
							<Select
								value={product}
								onChange={(option) => handleProductChange(option as ProductOption | null)}
								options={products}
								placeholder="Select Product"
							/>
						</div>
					</div>
					<div className="row mb-3">
						<div className="col-md-4">
							<label className="form-label">Product Code</label>
							<input type="text" className="form-control" value={productCode} readOnly />
						</div>
						<div className="col-md-4">
							<label className="form-label">Select BOM</label>
							<Select
								value={selectedBom}
								onChange={handleBomChange}
								options={bomOptions}
								placeholder="Select BOM"
							/>
						</div>
						<div className="col-md-4">
							<label className="form-label">Production Quantity (Ltr)</label>
							<input
								type="number"
								min={0}
								step="any"
								className="form-control"
								value={productionQuantity}
								onChange={(e) => handleProductionQtyChange(e.target.value)}
							/>
						</div>
					</div>
					<div className="row mb-5">
						<div className="col-md-4">
							<label className="form-label">Weight in KG</label>
							<input type="number" className="form-control" value={weightInKg} readOnly />
						</div>
						<div className="col-md-4">
							<label className="form-label">Batch No</label>
							<input
								type="text"
								className="form-control"
								value={batchNo}
								onChange={(e) => setBatchNo(e.target.value)}
							/>
						</div>
						<div className="col-md-4">
							<label className="form-label">Remarks</label>
							<textarea
								className="form-control"
								rows={2}
								value={remarks}
								onChange={(e) => setRemarks(e.target.value)}
							/>
						</div>
					</div>

					<div className="row mb-4">
						<div className="col-md-4">
							<label className="form-label">Base Oil Warehouse</label>
							<Select
								value={baseOilWarehouse}
								onChange={(option) => handleGroupWarehouseChange(option, BASE_OIL_CATEGORY, setBaseOilWarehouse)}
								options={warehouses}
								placeholder="Select Warehouse"
								isClearable
							/>
						</div>
						<div className="col-md-4">
							<label className="form-label">Additive Warehouse</label>
							<Select
								value={additiveWarehouse}
								onChange={(option) => handleGroupWarehouseChange(option, ADDITIVE_CATEGORY, setAdditiveWarehouse)}
								options={warehouses}
								placeholder="Select Warehouse"
								isClearable
							/>
						</div>
					</div>

					<h5 className="mb-3">Required Materials</h5>
					<Table size="sm" bordered hover>
						<thead>
							<tr>
								<th>SN</th>
								<th>Item Name</th>
								<th>Category</th>
								<th>Percentage</th>
								<th>Quantity</th>
								<th>Unit</th>
								<th>Warehouse</th>
								<th>Available Quantity</th>
							</tr>
						</thead>
						<tbody>
							{materials.length === 0 && (
								<tr>
									<td colSpan={8} className="text-center">No materials loaded. Select a product BOM.</td>
								</tr>
							)}
							{materials.map((row, index) => {
								const shortage = !row.idWarehouses || Number(row.quantity) > Number(row.availableQuantity) + 0.00001
								return (
									<tr key={row.idItems + '-' + index} className={shortage ? 'table-danger' : ''}>
										<td>{index + 1}</td>
										<td>{row.itemName}</td>
										<td>{row.categoryName}</td>
										<td>{row.percentage}</td>
										<td>{row.quantity}</td>
										<td>{row.unitName}</td>
										<td style={{ minWidth: '180px' }}>
											<Select
												value={warehouses.find((option) => Number(option.value) === Number(row.idWarehouses)) || null}
												onChange={(option) => handleWarehouseChange(option, index)}
												options={warehouses}
												placeholder="Select Warehouse"
												menuPosition="fixed"
												styles={{
													control: (provided) => ({
														...provided,
														minHeight: '32px',
														height: '32px',
													}),
												}}
											/>
										</td>
										<td>{formatDecimalQuantity(row.availableQuantity, 2)}</td>
									</tr>
								)
							})}
						</tbody>
					</Table>

					<div className="form-group row mt-4">
						<div className="col text-center">
							<button type="submit" className="btn btn-sm btn-primary me-2" disabled={!canSave}>
								<span>  Save</span>
							</button>
							<Button variant="secondary" size="sm" onClick={() => history.push('/blending-orders')}>
								Close
							</Button>
						</div>
					</div>
				</form>
			</div>
		</div>
	)
}

export default BlendingOrderCreate
